/**
 * @fileoverview Camera motion (RotateView / ZoomView / PanView / ResetCameraOrientation) for the
 * PyMOL backend, as pure functions over PyMOL's 18-float view.
 *
 * The motion itself is NOT re-derived here. Each function takes the PyMOL view to the backend-
 * neutral camera (visualizerCamera.js), expresses that as a 3Dmol view vector, applies the SAME
 * transform Visualizer3Dmol uses (util/viewTransform3Dmol.js), and comes back. So both backends
 * agree on every convention by construction - model-frame axes, right-handed angles, factor > 1
 * zooms in, the structure moves by +delta, the default pivot - and a fix there fixes both.
 * test/bmaps/pymolCameraMotion.test.js checks the agreement independently, by projecting model
 * points through each backend's own view.
 *
 * Two PyMOL-specific consequences:
 *   - The origin of rotation (v[12..14]) ends up on the view axis, at the centre of the view -
 *     where 3Dmol's pivot always is. Keeping PyMOL's own origin instead was tried and is wrong:
 *     that origin is attached to the model, 3Dmol's pivot to the camera, so after a pan or a
 *     rotation about an explicit pivot the next zoom or default-pivot rotation would act about
 *     a different point than 3Dmol's (the sequence test catches it). PyMOL's own `zoom`,
 *     `orient` and `origin` still move the origin as usual afterwards.
 *   - The clipping planes are kept where they were relative to the centre of the view: a zoom
 *     moves them by the change in camera distance, so it does not clip the structure away;
 *     rotation, pan and the orientation reset do not change that distance and keep the slab
 *     exactly. (pymolViewFromNeutral recentres the slab, which suits a live switch but would
 *     let a rotation change what a user's own `clip` shows.)
 *
 * Pure: no viewer, no DOM. VisualizerPyMOL queues these with its other camera operations.
 */

import {
    neutralFromPyMOLView, pymolViewFromNeutral, matrixToQuaternion, quaternionToMatrix,
} from '../visualizerCamera';
import { rotateViewAboutPoint, zoomView, panView } from '../util/viewTransform3Dmol';

/**
 * A nominal 3Dmol CAMERA_Z for the round trip. Only the camera distance (CAMERA_Z - view[3])
 * matters to the neutral camera, so the value cancels out; it only has to be finite.
 */
const CameraZ = 150;

const IdentityRotation = [1, 0, 0, 0, 1, 0, 0, 0, 1];

/** A valid PyMOL view, used to check a motion's arguments before it is queued. */
export const ProbeView = Object.freeze([
    1, 0, 0, 0, 1, 0, 0, 0, 1,
    0, 0, -50,
    0, 0, 0,
    40, 60,
    -20,
]);

/** @returns {number[]} the 3Dmol 8-float view that frames the same picture as neutral camera `n` */
function threeDmolViewFromNeutral(n) {
    const q = matrixToQuaternion(n.rotation);
    return [-n.center[0], -n.center[1], -n.center[2], CameraZ - n.distance, q.x, q.y, q.z, q.w];
}

/** The inverse of threeDmolViewFromNeutral; `fov` is carried through. */
function neutralFromThreeDmolView(v, fov) {
    return {
        center: [-v[0], -v[1], -v[2]],
        rotation: quaternionToMatrix({
            x: v[4], y: v[5], z: v[6], w: v[7],
        }),
        distance: CameraZ - v[3],
        fov,
    };
}

function assertValidPyMOLView(view, label) {
    if (!Array.isArray(view) || view.length !== 18 || !view.every(Number.isFinite)) {
        throw new TypeError(`${label} must be an array of 18 finite numbers, got: ${JSON.stringify(view)}`);
    }
    return view;
}

/**
 * The PyMOL view for neutral camera `after`, reached from `view` (whose neutral camera is
 * `before`): the slab keeps its place relative to the centre of the view.
 */
function toPyMOLView(view, before, after) {
    const moved = pymolViewFromNeutral(after, view);
    const shift = after.distance - before.distance;
    if (shift === 0) {
        moved[15] = view[15];
        moved[16] = view[16];
    } else {
        moved[15] = Math.max(1, view[15] + shift);
        moved[16] = Math.max(moved[15] + 1, view[16] + shift);
    }
    return assertValidPyMOLView(moved, 'moved view');
}

/**
 * Apply a 3Dmol view transform to a PyMOL view.
 * @param {number[]} view PyMOL 18-float view
 * @param {(v: number[]) => number[]} transform 8-float 3Dmol view -> 8-float 3Dmol view
 * @returns {number[]} a new PyMOL view; the input is never mutated
 */
function viaNeutral(view, transform) {
    assertValidPyMOLView(view, 'view');
    const before = neutralFromPyMOLView(view);
    const after = neutralFromThreeDmolView(transform(threeDmolViewFromNeutral(before)), before.fov);
    return toPyMOLView(view, before, after);
}

/**
 * Rotate by `degrees` about the model-frame `axis`, holding the model-space `pivot` fixed on
 * screen (default: the point at the centre of the view). Same contract as
 * Visualizer3Dmol.RotateView.
 * @param {number[]} view
 * @param {number} degrees positive = right-handed about `axis`
 * @param {'x'|'y'|'z'|[number,number,number]} axis
 * @param {[number,number,number]} [pivot]
 * @returns {number[]}
 */
export function rotatePyMOLView(view, degrees, axis, pivot) {
    return viaNeutral(view, (v) => rotateViewAboutPoint(v, degrees, axis, pivot));
}

/**
 * Zoom by `factor` (> 1 zooms in). Same contract as Visualizer3Dmol.ZoomView.
 * @param {number[]} view
 * @param {number} factor
 * @returns {number[]}
 */
export function zoomPyMOLView(view, factor) {
    return viaNeutral(view, (v) => zoomView(v, factor, CameraZ));
}

/**
 * Pan by a model-frame displacement in Angstroms; the structure moves by +delta. Same contract as
 * Visualizer3Dmol.PanView.
 * @param {number[]} view
 * @param {[number,number,number]} delta
 * @returns {number[]}
 */
export function panPyMOLView(view, delta) {
    return viaNeutral(view, (v) => panView(v, delta));
}

/**
 * Back to the orientation a structure is first drawn in (the identity rotation), keeping the
 * framing - the point at the centre of the view and the camera distance. Same contract as
 * Visualizer3Dmol.ResetCameraOrientation.
 * @param {number[]} view
 * @returns {number[]}
 */
export function resetPyMOLViewOrientation(view) {
    assertValidPyMOLView(view, 'view');
    const before = neutralFromPyMOLView(view);
    return toPyMOLView(view, before, { ...before, rotation: IdentityRotation });
}
